import React, { useState } from "react";
import { SectionHeader } from "@sprout/typography";
import Container from "@components/ui/container";
import { Checkbox } from "@sprout/checkbox";
import Layout from "@components/layout";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@sprout/flash.tsx";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";

const CATEGORIES = ["job_alerts", "dream_jobs"] as const;
type Category = (typeof CATEGORIES)[number];

interface EmailPreferencesProps {
  token: string;
  email: string;
  preferences: Record<string, boolean>;
  updatePath: string;
}

const EmailPreferences = ({ token, preferences, updatePath }: EmailPreferencesProps) => {
  const { t } = useTranslation("unsubscribe");
  const showFlash = useShowFlash();

  const initialValues = Object.fromEntries(
    CATEGORIES.map((category) => [category, preferences[`unsubscribe_${category}`]]),
  ) as Record<Category, boolean>;

  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const allChecked = CATEGORIES.every((category) => values[category]);

  const handleCategoryChange = (category: Category) => {
    setValues((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleAllChange = () => {
    const newValue = !allChecked;
    setValues(
      Object.fromEntries(CATEGORIES.map((category) => [category, newValue])) as Record<
        Category,
        boolean
      >,
    );
  };

  const handleCancel = () => window.history.back();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        CATEGORIES.map((category) => [`unsubscribe_${category}`, values[category]]),
      );
      await axios.patch(updatePath, {
        token,
        email_preference: { ...payload, unsubscribe_all: allChecked },
      });
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      showFlash(t("success"), "success");
    } catch {
      showFlash(t("error"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <SectionHeader as="h1">{t("title")}</SectionHeader>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col gap-3">
          {CATEGORIES.map((category) => (
            <Checkbox
              key={category}
              id={`unsubscribe-${category.replace(/_/g, "-")}`}
              options={[{ label: t(category), value: category }]}
              values={values[category] ? [category] : []}
              onSelect={() => handleCategoryChange(category)}
              isMulti
            />
          ))}
          <Checkbox
            id="unsubscribe-all"
            options={[{ label: t("unsubscribe_all"), value: "all" }]}
            values={allChecked ? ["all"] : []}
            onSelect={handleAllChange}
            isMulti
          />
          <div className="flex gap-3">
            <PrimaryButton type="submit" disabled={submitting}>
              {t("save")}
            </PrimaryButton>
            <SecondaryButton disabled={submitting} onClick={handleCancel}>
              {t("cancel")}
            </SecondaryButton>
          </div>
        </div>
      </form>
    </Container>
  );
};

EmailPreferences.layout = (page: React.ReactNode) => <Layout publicFacing>{page}</Layout>;

export default EmailPreferences;
